import React, { useState } from 'react';
import { useWallet } from '../../contexts/WalletContext';

const WalletConnector = ({ onClose, onSuccess }) => {
  const { connectExternalWallet, isConnecting, error, clearError } = useWallet();
  const [walletAddress, setWalletAddress] = useState('');
  const [validationError, setValidationError] = useState('');

  const validateSolanaAddress = (address) => {
    // Basic Solana address validation
    if (!address || address.length < 32 || address.length > 44) {
      return 'Solana address must be between 32-44 characters';
    }
    
    // Check for valid Base58 characters
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    if (!base58Regex.test(address)) {
      return 'Invalid characters in Solana address';
    }
    
    return '';
  };

  const handleAddressChange = (e) => {
    const address = e.target.value.trim();
    setWalletAddress(address);
    
    // Clear previous errors
    setValidationError('');
    clearError();
    
    // Validate on input
    if (address) {
      const error = validateSolanaAddress(address);
      setValidationError(error);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    
    // Final validation
    const error = validateSolanaAddress(walletAddress);
    if (error) {
      setValidationError(error);
      return;
    }
    
    const result = await connectExternalWallet(walletAddress);
    
    if (result.success) {
      if (onSuccess) {
        onSuccess(result.wallet);
      }
      if (onClose) {
        onClose();
      }
    }
  };

  const handlePhantomConnect = () => {
    // Placeholder for future Phantom wallet integration
    setValidationError('Phantom wallet integration coming soon. Please enter your wallet address manually for now.');
  };

  const handleSolflareConnect = () => {
    // Placeholder for future Solflare wallet integration
    setValidationError('Solflare wallet integration coming soon. Please enter your wallet address manually for now.');
  };

  return (
    <div className="wallet-connector">
      <div className="wallet-connector-header">
        <h3>Connect External Wallet</h3>
        <button 
          className="close-button"
          onClick={onClose}
          disabled={isConnecting}
        >
          ×
        </button>
      </div>
      
      <div className="wallet-connector-content">
        <div className="connection-info">
          <div className="info-banner">
            <div className="info-icon">🚀</div>
            <div className="info-content">
              <h4>Direct Wallet Integration Coming Soon!</h4>
              <p>We're working on direct integration with Phantom, Solflare, and other popular wallets. For now, you can connect by entering your wallet address manually.</p>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleConnect} className="manual-connection">
          <h4>Enter Wallet Address Manually</h4>
          <div className="form-group">
            <label htmlFor="walletAddress">Solana Wallet Address</label>
            <input
              type="text"
              id="walletAddress"
              value={walletAddress}
              onChange={handleAddressChange}
              placeholder="Enter your Solana wallet address (e.g., 7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU)"
              disabled={isConnecting}
              className={validationError ? 'error' : ''}
            />
            {validationError && (
              <div className="field-error">
                <span className="error-icon">❌</span>
                {validationError}
              </div>
            )}
            <div className="field-help">
              <p>You can find your wallet address in your Solana wallet app (Phantom, Solflare, etc.)</p>
            </div>
          </div>
          
          {error && (
            <div className="error-message">
              <span className="error-icon">❌</span>
              {error}
            </div>
          )}
          
          <div className="security-note">
            <div className="note-icon">🔒</div>
            <div className="note-content">
              <p><strong>Security Note:</strong> We only store your wallet address, not your private key. You'll need to sign transactions manually in your wallet app.</p>
            </div>
          </div>
          
          <div className="button-group">
            <button 
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={isConnecting}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="connect-button"
              disabled={isConnecting || !walletAddress || validationError}
            >
              {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WalletConnector;